import * as React from 'react';
import { ConsumerPurchaseSummaryFragment } from 'generated/graphql';
import { PlainButton } from 'omakase';
import { ReservationPreviewCardFooter } from 'components/profile/ReservationPreviewCardFooter';
import { centerCropUrl } from '@tock/images';
import { core } from '@tock/coretypes';
import { getReservationLink } from 'utils/profileUtils';
import { isOrderVariety } from '@tock/shared/utils/offerings';
import { preventBubbleOutside } from '@tock/shared/utils/eventUtils';
import Anchor from 'components/Anchor';
import LazyImage from '@tock/shared/components/LazyImage';
import ReservationDetails from 'components/profile/ReservationDetails';

interface Props {
  patron: core.IPatron;
  reservation: ConsumerPurchaseSummaryFragment;
  showManageReservation: boolean;
}

const ReservationPreviewCard = ({
  patron,
  reservation,
  reservation: { business },
  showManageReservation,
}: Props) => {
  const image = business ? business.profileImages[0] : null;
  const manageLink = getReservationLink(reservation);
  const orderOrReservation = isOrderVariety(reservation.ticketType?.variety || undefined)
    ? 'order'
    : 'reservation';
  const showFeedback =
    (reservation.eligibleForFeedback && reservation.dinerPatron?.email === patron?.email) ||
    reservation.visitFiveStarRating;

  // @todo the anchor wrapper element and buttons create an invalid nesting scenario
  return (
    <Anchor
      className="ReservationPreviewCard ReservationPreviewCard--feedback"
      tabIndex={-1}
      href={manageLink}
      data-testid="history-reservation-link"
    >
      <div className="ReservationPreviewCard-data">
        <div className="ReservationPreviewCard-details ReservationPreviewCard-details--feedback">
          <ReservationDetails
            reservation={reservation}
            title={(business && business.name) || undefined}
            size="LARGE"
          />
          {showManageReservation ? (
            <PlainButton
              component={Anchor}
              variant="primary"
              size="small"
              href={manageLink}
              data-testid="manage-reservation-button"
            >
              Manage {orderOrReservation}
            </PlainButton>
          ) : (
            !!business.domainName && (
              <PlainButton
                component={Anchor}
                variant="primary"
                size="small"
                href={`/${business.domainName}`}
                onClick={(event: React.MouseEvent) => preventBubbleOutside(event)}
              >
                Book again
              </PlainButton>
            )
          )}
        </div>
        {image && image.backingUrl && (
          <div className="ReservationPreviewCard-imageWrapper">
            <LazyImage
              src={centerCropUrl(image.backingUrl, { width: 350, height: 350 })}
              alt={image.altText || undefined}
              backgroundColor={image.dominantColor || undefined}
              className="ReservationPreviewCard-image"
            />
          </div>
        )}
      </div>
      {showFeedback && (
        <ReservationPreviewCardFooter
          reservationId={reservation.id}
          businessDomain={business.domainName}
          rating={reservation.visitFiveStarRating}
        />
      )}
    </Anchor>
  );
};

export default ReservationPreviewCard;
